import {AttrsSchema, Component} from 'panel';

import {serializeAttributes} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {BillingAccount} from 'common/report/queries/app-api/support-info-client';

import {SupportWidgetFormState} from './support-widget-form';
import {template} from './template';
import type {SupportPlan} from './support-widget-form/types';

export type SupportWidgetAttrs = {
  elref?: string;
  'billing-account': BillingAccount;
  'support-plan': SupportPlan;
  'events-plan-id': number;
  'feature-gate-name'?: string;
  'people-plan-id': number;
  'project-id': number;
  'total-arr'?: number;
  'user-age-in-days': number;
  'user-email': string;
  'user-id': number;
  'user-name': string;
  'should-override-free-warning': boolean;
};

type SupportWidgetState = {
  isMaximized: boolean;
};

type SupportWidgetProps = {
  maximize: boolean;
};

type CloseWidgetEvent = CustomEvent<{isMaximized: boolean}>;

type SupportWidgetJsxProps = {
  attrs: SupportWidgetAttrs;
  props: SupportWidgetProps;
  on: {
    closeWidget: (ev: CloseWidgetEvent) => void;
  };
};

export class SupportWidget extends Component<SupportWidgetState, SupportWidgetAttrs> {
  private declare formState: SupportWidgetFormState;

  static get attrsSchema(): AttrsSchema<SupportWidgetAttrs> {
    return {
      'billing-account': {type: `json`, description: `interface: BillingAccount`},
      'support-plan': `string`,
      'events-plan-id': `number`,
      'feature-gate-name': `string`,
      'people-plan-id': `number`,
      'project-id': `number`,
      'total-arr': `number`,
      'user-age-in-days': `number`,
      'user-email': `string`,
      'user-id': `number`,
      'user-name': `string`,
      'should-override-free-warning': `boolean`,
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      defaultState: {
        isMaximized: false,
      },
      helpers: {
        handleSupportWidgetClosed: () => {
          this.dispatchEvent(new CustomEvent(`closeWidget`));
        },

        handleSupportWidgetMaximized: () => {
          this.dispatchEvent(new CustomEvent(`maximize`, {detail: {isMaximized: true}}));
          this.update({isMaximized: true});
        },

        handleSupportWidgetUnmaximized: () => {
          this.dispatchEvent(new CustomEvent(`maximize`, {detail: {isMaximized: false}}));
          this.update({isMaximized: false});
        },

        handleFormStateChange: (ev: any) => {
          this.formState = ev.detail;
        },

        getFormAttributes: () => {
          const newFormState: SupportWidgetFormState = Object.assign({}, this.formState, {
            isMaximized: this.state.isMaximized,
            isUploadingFiles: false,
          });
          return serializeAttributes({
            'data-state': newFormState,
            ...this.attrs(),
          });
        },
      },
    };
  }

  // Opens the support widget in a maximized state
  set maximize(isMaximized: boolean) {
    this.update({isMaximized});
  }
}

export const SupportWidgetEl = defineAndMakeJsxFactory<SupportWidgetJsxProps>(`mp-support-widget`, SupportWidget);
export const SupportWidgetReact = panel2react<SupportWidgetJsxProps>(`mp-support-widget`, SupportWidget);
